import {
  AppDivider,
  AppEmptyState,
  AppScreenScaffold,
  AppTopBar,
  Icon,
  IconButton,
  type IconName,
} from '@core/designsystem';
import type { EventType } from '@core/domain/model';
import { WEEKDAYS_SHORT } from '@core/ui/RuDates';
import { NewEventDialog } from '@core/ui/NewEventDialog';
import type { CalendarActions, CalendarState } from './useCalendar';
import './calendar.css';

const TYPE_ICON: Record<EventType, IconName> = {
  Hearing: 'gavel',
  Meeting: 'group',
  Task: 'checklist',
  Other: 'event',
};

/**
 * Календарь: месяц и день на одном экране — «Структура» §9.1.
 *
 * Разделять их на два экрана нельзя: юрист смотрит на месяц ровно затем, чтобы
 * увидеть, что в конкретный день, и лишний переход отнимал бы у этого весь смысл.
 */
export function CalendarScreen({
  state,
  actions,
  onOpenEvent,
  onOpenClient,
  creating = false,
  onCreatingChange,
}: {
  state: CalendarState;
  actions: CalendarActions;
  onOpenEvent: (id: string) => void;
  onOpenClient: (id: string) => void;
  creating?: boolean;
  onCreatingChange?: (creating: boolean) => void;
}) {
  return (
    <AppScreenScaffold
      topBar={
        <AppTopBar
          title="Календарь"
          root
          actions={
            <>
              <IconButton icon="chevron_right" label="Предыдущий месяц" onClick={actions.previousMonth} className="calendar__back" />
              <IconButton icon="chevron_right" label="Следующий месяц" onClick={actions.nextMonth} />
            </>
          }
        />
      }
      overlay={
        creating ? (
          <NewEventDialog
            date={state.selectedDate}
            cases={state.cases}
            onDismiss={() => onCreatingChange?.(false)}
            onCreate={(draft) => {
              void actions.createEvent(draft);
              onCreatingChange?.(false);
            }}
          />
        ) : null
      }
    >
      <div className="calendar__month">
        <button type="button" className="calendar__month-label" onClick={actions.today}>
          {state.monthLabel}
        </button>
      </div>

      <div className="calendar__weekdays">
        {WEEKDAYS_SHORT.map((day) => (
          <span key={day} className="calendar__weekday">
            {day}
          </span>
        ))}
      </div>

      <div className="calendar__grid">
        {state.days.map((day) => (
          <button
            key={day.date}
            type="button"
            className={
              `calendar__day ` +
              `${day.isCurrentMonth ? '' : 'calendar__day--other'} ` +
              `${day.isToday ? 'calendar__day--today' : ''} ` +
              `${day.isSelected ? 'calendar__day--selected' : ''}`
            }
            aria-pressed={day.isSelected}
            onClick={() => actions.selectDate(day.date)}
          >
            <span className="calendar__day-number">{day.dayOfMonth}</span>
            <span className="calendar__dots">
              {/* Точка под числом красится по самому важному, что есть в этот день. */}
              {day.hasHearing ? <span className="calendar__dot calendar__dot--hearing" /> : null}
              {day.hasOtherEvents ? <span className="calendar__dot" /> : null}
              {day.hasBirthday ? <span className="calendar__dot calendar__dot--birthday" /> : null}
            </span>
          </button>
        ))}
      </div>

      <AppDivider />

      <h2 className="calendar__day-title">{state.selectedLabel}</h2>

      {state.events.length === 0 && !state.isLoading ? (
        <AppEmptyState icon="event" text="В этот день ничего не назначено" />
      ) : null}

      <div className="calendar__events">
        {state.events.map((event) => (
          <button
            key={event.id}
            type="button"
            className="calendar-event"
            onClick={() =>
              event.clientId ? onOpenClient(event.clientId) : onOpenEvent(event.id)
            }
          >
            <Icon
              name={event.clientId ? 'person' : TYPE_ICON[event.type]}
              size={22}
              className="calendar-event__icon"
            />
            <span className="calendar-event__texts">
              <span className={`calendar-event__title ${event.isCompleted ? 'calendar-event__title--done' : ''}`}>
                {event.title}
              </span>
              <span className="calendar-event__subtitle">{event.subtitle}</span>
            </span>
            {event.time ? <span className="calendar-event__time">{event.time}</span> : null}
          </button>
        ))}
      </div>
    </AppScreenScaffold>
  );
}

